import { LanguagesEnum } from '@/api/enums';
import { Course } from '@/client/courses';
import { Actions, User } from '@/client/users';
import {
  learnerOnlyLanguages,
  rtlLanguages,
} from '@/common/constants/languages';
import { isUserLanguageAvailableInCourse } from '@/utils/helpers/courses.helper';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';
import { PureAbility } from '@casl/ability';

export const isLanguageAllowedForAdmin = (
  language: LanguagesEnum | string | undefined | null,
  userFeatureAbility: PureAbility | null | undefined,
): boolean => {
  if (!language) {
    return false;
  }

  if (!learnerOnlyLanguages.includes(language as LanguagesEnum)) {
    return true;
  }

  if (language === LanguagesEnum.NO) {
    return !!userFeatureAbility?.can(
      Actions.READ,
      PostHogFeatureFlags.ADMIN_UI_NORWEGIAN,
    );
  }

  return false;
};

export const determineAttemptLanguage = (
  userLanguage: string | LanguagesEnum,
  supportedLanguages: LanguagesEnum[],
  urlOverride?: string | null,
): LanguagesEnum => {
  if (
    urlOverride &&
    supportedLanguages.includes(urlOverride as LanguagesEnum)
  ) {
    return urlOverride as LanguagesEnum;
  }

  if (
    userLanguage &&
    supportedLanguages.includes(userLanguage as LanguagesEnum)
  ) {
    return userLanguage as LanguagesEnum;
  }

  return LanguagesEnum.EN;
};

export const resolveAttemptLanguage = (
  course: Course,
  selectedLanguage?: LanguagesEnum,
): LanguagesEnum => {
  if (course.selfHosted && course.courseLanguages?.length === 1) {
    return course.courseLanguages[0];
  }

  if (!course.selfHosted) {
    return LanguagesEnum.EN;
  }

  return selectedLanguage || LanguagesEnum.EN;
};

export const determinePreselectedCourseLanguage = (
  currentUser: User | null,
  course: Course | null,
): LanguagesEnum => {
  // Case 0: Course has only one language
  if (course?.courseLanguages?.length === 1) {
    return course.courseLanguages[0];
  }

  // Case 1: User has a specific content language and it's available in the course
  if (
    currentUser?.contentLanguage &&
    course &&
    isUserLanguageAvailableInCourse(
      course.courseLanguages,
      currentUser.contentLanguage,
    )
  ) {
    return currentUser.contentLanguage;
  }

  // Case 2: Use the user UI preferred language if it's available in the course
  if (
    currentUser?.language &&
    course &&
    isUserLanguageAvailableInCourse(
      course.courseLanguages,
      currentUser.language as LanguagesEnum,
    )
  ) {
    return currentUser.language as LanguagesEnum;
  }

  // Case 3: Fallback to course default language or English
  return course?.defaultLanguage || LanguagesEnum.EN;
};

export const determineCourseExportLanguage = (
  course: Course | null,
): LanguagesEnum => {
  const contentLanguages = course?.courseLanguages ?? [];

  if (
    course?.defaultLanguage &&
    contentLanguages.includes(course.defaultLanguage)
  ) {
    return course.defaultLanguage;
  }

  return contentLanguages[0] ?? course?.defaultLanguage ?? LanguagesEnum.EN;
};

export const isRtlLanguage = (language: LanguagesEnum | undefined): boolean => {
  return language ? rtlLanguages.includes(language) : false;
};

export const resolveTextDirection = (
  language: LanguagesEnum | string | undefined,
): 'rtl' | 'ltr' => (isRtlLanguage(language as LanguagesEnum) ? 'rtl' : 'ltr');

export const toBcp47 = (
  language: LanguagesEnum | string,
  { includeRegion = false }: { includeRegion?: boolean } = {},
): string =>
  includeRegion ? language.replace('_', '-') : language.split('_')[0];
